import React from 'react'

import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Text } from '@wartek-id/text'
import { Spinner } from '@wartek-id/spinner'

import PDFPreviewer from 'app/BuktiKaryaPage/components/PDFPreviewer'
import Header from 'app/BuktiKaryaPage/components/Header'
import useKSPSTrackEvent from '../hooks/useKSPSTrackEvent'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { BCKSDesktopPageShell } from 'app/BCKSPage/components/BCKSDesktopPageShell'

const ONE_MEGABYTE = 1048576

export const DocUploader = (props) => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()
  const {
    docData,
    setDoc,
    selectedDocMeta,
    onDocUpload,
    isReviewMode,
    onBackClick,
  } = props
  const [file, setFile] = React.useState(docData.fileData)

  const toast = useToast()
  const [fileData, setFileData] = React.useState(null)
  const [pageLoaded, setPageLoaded] = React.useState(false)
  const [fileIsLoading, setFileIsLoading] = React.useState(true)
  const [showUploadRunningModal, setShowUploadRunningModal] =
    React.useState(false)
  const inputFileRef = React.useRef(null)

  const onFileChange = (event) => {
    let file = event.target.files[0]
    if (file) {
      if (checkFileSizeUnderLimit(file.size)) {
        setFile(file)
        setFileIsLoading(true)
      } else
        toast({
          message: 'maksimum file yang diizinkan 5 Megabytes',
        })
    }
  }

  React.useEffect(() => {
    let fileReader,
      isCancel = false
    if (file) {
      fileReader = new FileReader()
      fileReader.onload = (e) => {
        const { result } = e.target
        let typedarray = new Uint8Array(result)
        if (result && !isCancel) {
          setFileData(typedarray)
        }
      }
      fileReader.readAsArrayBuffer(file)
    }
    return () => {
      isCancel = true
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort()
      }
    }
  }, [file])

  React.useEffect(() => {
    const onPageLoad = () => {
      setPageLoaded(true)
      setFileIsLoading(false)
    }

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      onPageLoad()
    } else {
      window.addEventListener('load', onPageLoad)
      // Remove the event listener when component unmounts
      return () => window.removeEventListener('load', onPageLoad)
    }
  }, [])

  const onFilePreviewClick = () => {
    if (pageLoaded) inputFileRef.current.click()
  }

  const checkFileSizeUnderLimit = (number: number) => {
    return number < ONE_MEGABYTE * 5
  }

  const onUploadButtonClick = (e) => {
    e.preventDefault()
    KSPSTrackEvent({
      event_name: 'click_file_collection_upload',
    })
    const payload: FormData = new FormData()
    payload.append('file', file)
    setShowUploadRunningModal(true)
    onDocUpload(selectedDocMeta.name, {
      name: file.name,
      url: 'local',
      fileData: file,
    })

    setShowUploadRunningModal(false)
    setDoc?.({ docName: null, pageTitle: null })
  }

  const handleBackClick = () => {
    if (isReviewMode) {
      return onBackClick()
    }
    setDoc?.({ docName: null, pageTitle: null })
  }

  const { isDesktop } = useBCKSDesktopView()

  if (isDesktop) {
    return (
      <BCKSDesktopPageShell
        title={selectedDocMeta.title}
        pageLabel={selectedDocMeta.title}
        rawContent
      >
        <div className="max-w-6xl mx-auto">
          <Dialog
            isOpen={showUploadRunningModal}
            onClose={() => setFileIsLoading(false)}
          >
            <DialogOverlay />
            <DialogContent>
              <div className="flex flex-row content-around sticky w-full p-2">
                <Spinner size="lg" />
                <Text as="span" className="pl-4" variant="body">
                  Harap Tunggu, Dokumen Sedang Diupload
                </Text>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog
            isOpen={fileIsLoading}
            onClose={() => setShowUploadRunningModal(false)}
          >
            <DialogOverlay />
            <DialogContent>
              <div className="flex flex-row content-around sticky w-full p-2">
                <Text as="span" className="pl-4" variant="body">
                  Harap Tunggu, Tinjauan File Sedang Diproses
                </Text>
              </div>
            </DialogContent>
          </Dialog>

          <div className="h-auto bg-neutral-100">
            <input
              className="hidden"
              type="file"
              onChange={onFileChange}
              accept="application/pdf"
              ref={inputFileRef}
              data-testid={`field-${selectedDocMeta.title}`}
            />
            <div className="min-h-[100vh]">
              {docData.url || isReviewMode ? (
                <PDFPreviewer
                  data={fileData || docData.url}
                  onFilePreviewClick={onFilePreviewClick}
                  pageLoaded={pageLoaded}
                  setFileIsLoading={setFileIsLoading}
                  isRemoteUrl={!fileData}
                />
              ) : (
                <PDFPreviewer
                  data={fileData}
                  onFilePreviewClick={onFilePreviewClick}
                  pageLoaded={pageLoaded}
                  setFileIsLoading={setFileIsLoading}
                />
              )}
            </div>
          </div>

          <div className="flex flex-row content-around w-full p-4 bg-white">
            <Button
              color="white"
              fullWidth
              onClick={onFilePreviewClick}
              disabled={isReviewMode ? false : !fileData}
            >
              Ganti Dokumen
            </Button>
            <Button
              className="ml-4"
              color="black"
              fullWidth
              disabled={!fileData}
              onClick={onUploadButtonClick}
            >
              Unggah
            </Button>
          </div>
        </div>
      </BCKSDesktopPageShell>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <div className="fixed w-full z-10">
        <Header
          title={selectedDocMeta.title}
          forceNavigateBack
          onBackClick={handleBackClick}
        />
      </div>

      <Dialog
        isOpen={showUploadRunningModal}
        onClose={() => setFileIsLoading(false)}
      >
        <DialogOverlay />
        <DialogContent>
          <div className="flex flex-row content-around sticky w-full p-2">
            <Spinner size="lg" />
            <Text as="span" className="pl-4" variant="body">
              Harap Tunggu, Dokumen Sedang Diupload
            </Text>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        isOpen={fileIsLoading}
        onClose={() => setShowUploadRunningModal(false)}
      >
        <DialogOverlay />
        <DialogContent>
          <div className="flex flex-row content-around sticky w-full p-2">
            <Text as="span" className="pl-4" variant="body">
              Harap Tunggu, Tinjauan File Sedang Diproses
            </Text>
          </div>
        </DialogContent>
      </Dialog>

      <div className="h-auto bg-neutral-100">
        <input
          className="hidden"
          type="file"
          onChange={onFileChange}
          accept="application/pdf"
          ref={inputFileRef}
          data-testid={`field-${selectedDocMeta.title}`}
        />
        <div className="mt-12 min-h-[100vh]">
          {docData.url || isReviewMode ? (
            <PDFPreviewer
              data={fileData || docData.url}
              onFilePreviewClick={onFilePreviewClick}
              pageLoaded={pageLoaded}
              setFileIsLoading={setFileIsLoading}
              isRemoteUrl={!fileData}
            />
          ) : (
            <PDFPreviewer
              data={fileData}
              onFilePreviewClick={onFilePreviewClick}
              pageLoaded={pageLoaded}
              setFileIsLoading={setFileIsLoading}
            />
          )}
        </div>
      </div>

      <div className="flex flex-row content-around sticky w-full p-4 left-0 bottom-0 bg-white ">
        <Button
          color="white"
          fullWidth
          onClick={onFilePreviewClick}
          disabled={isReviewMode ? false : !fileData}
        >
          Ganti Dokumen
        </Button>
        <Button
          className="ml-4"
          color="black"
          fullWidth
          disabled={!fileData}
          onClick={onUploadButtonClick}
        >
          Unggah
        </Button>
      </div>
    </div>
  )
}
